import { getCurrentOrganizationUsage } from "@/api/endpoints/organization";
import {
  LANGUAGE_CODE_TO_LABEL,
  LANGUAGES,
  PLAN_TYPE,
  PLAN_TYPE_TO_LABEL,
  SOURCE_CODE_TO_LABEL,
  SOURCES,
} from "@/constants";
import { useOrganization } from "@/hooks/useOrganization";
import { Badge } from "@components/ui/badge";
import { Button } from "@components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@components/ui/card";
import Error from "@components/ui/error";
import { Progress } from "@components/ui/progress";
import { Skeleton } from "@components/ui/skeleton";
import { i18n } from "@lingui/core";
import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { ExternalLinkIcon } from "lucide-react";
import { Link, useParams } from "react-router-dom";

export default function AccountPlan() {
  const { t } = useLingui();

  // URL state
  const { orgUuid } = useParams<{ orgUuid: string }>();

  // Server state
  const {
    data: orgData,
    error: orgError,
    isLoading: isOrgLoading,
  } = useOrganization();

  const {
    data: usageData,
    error: usageError,
    isLoading: isUsageLoading,
  } = useQuery({
    queryKey: [orgUuid, "usage"],
    queryFn: getCurrentOrganizationUsage,
    enabled: !!orgUuid,
  });

  return (
    <Card className="p-0 gap-0" data-testid="plan-info">
      <CardHeader className="p-6 pb-4 flex items-center gap-3">
        <CardTitle className="text-xl -translate-y-0.5">
          <Trans>Abonnemang</Trans>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-6 pb-6 gap-6">
        {orgData && usageData ? (
          orgData.ok && usageData.ok ? (
            <>
              <div className="flex items-center gap-3">
                <p className="text-lg font-semibold" data-testid="plan-type">
                  {i18n._(PLAN_TYPE_TO_LABEL[orgData.data.plan.type])}
                </p>
                {orgData.data.plan_subscriptions?.[0]?.end_date ? (
                  <Badge shape="square" variant="secondary">
                    <Trans>Förnyas</Trans>{" "}
                    {format(orgData.data.plan_subscriptions[0].end_date, "PP")}
                  </Badge>
                ) : null}
              </div>

              <div className="flex flex-col gap-3">
                <p className="font-semibold">
                  {usageData.data.num_active_alerts} <Trans>av</Trans>{" "}
                  {usageData.data.max_num_alerts}{" "}
                  <Trans>bevakningar använda</Trans>
                </p>
                <Progress
                  value={
                    (usageData.data.num_active_alerts /
                      usageData.data.max_num_alerts) *
                    100
                  }
                />
              </div>

              {usageData.data.max_netfeedr_priority_keywords > 0 && (
                <div className="flex flex-col gap-3">
                  <p className="font-semibold">
                    {usageData.data.num_netfeedr_priority_keywords}{" "}
                    <Trans>av</Trans>{" "}
                    {usageData.data.max_netfeedr_priority_keywords}{" "}
                    <Trans>LinkedIn-nyckelord använda</Trans>
                  </p>
                  <Progress
                    value={
                      (usageData.data.num_netfeedr_priority_keywords /
                        usageData.data.max_netfeedr_priority_keywords) *
                      100
                    }
                  />
                </div>
              )}

              <div className="flex items-start gap-x-12 gap-y-4 flex-wrap">
                <div className="flex flex-col gap-2">
                  <p className="text-sm text-muted-foreground">
                    <Trans>Medietyper</Trans>
                  </p>
                  <p className="font-semibold text-balance">
                    {usageData.data.available_sources.length ===
                    SOURCES.length ? (
                      <Trans>Alla</Trans>
                    ) : (
                      usageData.data.available_sources
                        .map((source) => i18n._(SOURCE_CODE_TO_LABEL[source]))
                        .join(", ")
                    )}
                  </p>
                </div>

                <div className="flex flex-col gap-2">
                  <p className="text-sm text-muted-foreground">
                    <Trans>Transkriptionsspråk</Trans>
                  </p>
                  <p
                    className="font-semibold text-balance"
                    data-testid="available-languages"
                  >
                    {usageData.data.available_languages.length ===
                    LANGUAGES.length ? (
                      <Trans>Alla språk</Trans>
                    ) : (
                      usageData.data.available_languages
                        .map((language) =>
                          i18n._(LANGUAGE_CODE_TO_LABEL[language])
                        )
                        .join(", ")
                    )}
                  </p>
                </div>
              </div>

              {(orgData.data.plan.type === PLAN_TYPE.FREE ||
                orgData.data.plan.type === PLAN_TYPE.BASIC) && (
                <Button
                  render={<Link to={{ search: "?upsale=true" }} />}
                  nativeButton={false}
                  className="w-fit mt-2"
                >
                  <Trans>Uppgradera plan</Trans>
                  <ExternalLinkIcon />
                </Button>
              )}
            </>
          ) : (
            <Error
              heading={t`Åh nej!`}
              description={t`Något gick fel när vi försökte ladda in din abonnemangsinformation.`}
            />
          )
        ) : isOrgLoading || isUsageLoading ? (
          <>
            <Skeleton className="h-6 w-24 animate-out" />
            <Skeleton className="h-5 w-56" />
            <Skeleton className="h-2 w-full" />
            <Skeleton className="h-12 w-full" />
          </>
        ) : orgError || usageError ? (
          <Error
            heading={t`Åh nej!`}
            description={t`Något gick fel när vi försökte ladda in din abonnemangsinformation.`}
            error={orgError || usageError}
          />
        ) : null}
      </CardContent>
    </Card>
  );
}
